/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { Category, categories } from '../common/Categories';
import { loadFromLocalStorage } from '../common/localStorage';
import { getUrlByCategory } from '../common/constants';
import { GridGraphqlExtras, toServiceCaseGridGraphqlBody } from '../common/graphqlJsonBody';

const requestGridDataType = 'REQUEST_GRID_DATA';
const receiveGridDataType = 'RECEIVE_GRID_DATA';
const errorType = 'ERROR_TYPE';

// Track abort controllers per category to cancel in-flight requests
let abortControllers: Record<number, AbortController> = {};

const initialState = {
    gridData: {},
    isLoading: false,
    // Cache results per category to avoid refetching on tab switches
    cache: {} as Record<number, { gridData: any; totalCount?: number; timestamp: number }>,
    lastRequestKey: '' as string
}

export const actionCreators = {

    requestGridData: (id: number, startDate: Date, endDate: Date, extras: GridGraphqlExtras, requestBodyForGrid: string) => async (dispatch: any, getState: any) => {
        const state = getState().dataTable || getState();
        const extrasKey = JSON.stringify(extras || {});
        const requestKey = `${id}|${startDate?.toString()}|${endDate?.toString()}|${extrasKey}|${requestBodyForGrid}`;
        
        // Skip exact duplicate requests
        if (state.lastRequestKey === requestKey && state.isLoading) {
            return;
        }

        let selectedCategory: Category | undefined = categories.find((category: Category) => {
            return category.id === id;
        });
        selectedCategory = selectedCategory == undefined ? categories[0] : selectedCategory;
        
        // Check cache for this category (without filters) and serve immediately if fresh
        const cached = state.cache?.[id];
        const cacheMaxAge = 60000; // 1 minute
        const hasGridArgs = !!(extras && (extras.orderby || (extras.filters && extras.filters.length) || extras.pageno || extras.pagesize));
        if (cached && !hasGridArgs && !requestBodyForGrid && (Date.now() - cached.timestamp < cacheMaxAge)) {
            const responseData = mapCachedToResponse(id, cached);
            dispatch({ type: receiveGridDataType, selectedCategory, responseData, fromCache: true });
            return;
        }

        // Cancel any in-flight request for this category
        if (abortControllers[id]) {
            abortControllers[id].abort();
        }
        abortControllers[id] = new AbortController();
        
        dispatch({ type: requestGridDataType, id, startDate, endDate, selectedCategory, requestKey });

        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
            'Content-Type': 'application/json'
        };
        const url = getUrlByCategory(selectedCategory);
        try {
            if (navigator.onLine) {
                await fetch(url, {
                    headers: new Headers(headerDict),
                    body: toServiceCaseGridGraphqlBody(
                            selectedCategory.requestBodyForGrid.replace('%r', requestBodyForGrid),
                            selectedCategory.id,
                            startDate,
                            endDate,
                            extras),
                    method: 'POST',
                    signal: abortControllers[id]?.signal
                }).then(async (response: any) => {
                    const responseData = await response.json();
                    switch (response.status) {
                        case 200:
                            dispatch({ type: receiveGridDataType, selectedCategory, responseData });
                            break;
                        case 400:
                            dispatch({ type: errorType, e: { message: 'Bad Request' } });
                            break;
                        case 500:
                            dispatch({ type: errorType, e: { message: 'Internal server error' } });
                            break;
                        case 401:
                            dispatch({ type: errorType, e: { code: 401, message: responseData.data } });
                            break;
                    }
                    return responseData;
                });
            }
        }
        catch (e) {
            // Ignore abort errors quietly
            if ((e as any)?.name === 'AbortError') {
                return;
            }
            if (navigator.onLine) {
                dispatch({ type: errorType, e });
            }
        }
    }
}

export const reducer = (state: any, action: any) => {
    state = state || initialState;
    switch (action.type) {
        case requestGridDataType:
            return {
                ...state,
                isLoading: true,
                lastRequestKey: action.requestKey || state.lastRequestKey
            };
        case receiveGridDataType:
            switch (action.selectedCategory.id) {
                case 0:
                    return {
                        ...state,
                        gridData: action.responseData.data.myopenservicecases.data,
                        totalCount: action.responseData.data.myopenservicecases.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            0: { gridData: action.responseData.data.myopenservicecases.data, totalCount: action.responseData.data.myopenservicecases.count, timestamp: Date.now() }
                        }
                    };
                case 1:
                    return {
                        ...state,
                        gridData: action.responseData.data.myclosedservicecases.data,
                        totalCount: action.responseData.data.myclosedservicecases.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            1: { gridData: action.responseData.data.myclosedservicecases.data, totalCount: action.responseData.data.myclosedservicecases.count, timestamp: Date.now() }
                        }
                    };
                case 2:
                    return {
                        ...state,
                        gridData: action.responseData.data.mycompanyopenservicecases.data,
                        totalCount: action.responseData.data.mycompanyopenservicecases.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            2: { gridData: action.responseData.data.mycompanyopenservicecases.data, totalCount: action.responseData.data.mycompanyopenservicecases.count, timestamp: Date.now() }
                        }
                    };
                case 3:
                    return {
                        ...state,
                        gridData: action.responseData.data.mycompanyclosedservicecases.data,
                        totalCount: action.responseData.data.mycompanyclosedservicecases.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            3: { gridData: action.responseData.data.mycompanyclosedservicecases.data, totalCount: action.responseData.data.mycompanyclosedservicecases.count, timestamp: Date.now() }
                        }
                    };
                case 4:
                    return {
                        ...state,
                        gridData: action.responseData.data.mylicenses.data,
                        totalCount: action.responseData.data.mylicenses.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            4: { gridData: action.responseData.data.mylicenses.data, totalCount: action.responseData.data.mylicenses.count, timestamp: Date.now() }
                        }
                    };
                case 5:
                    return {
                        ...state,
                        gridData: action.responseData.data.allservicecases.data,
                        totalCount: action.responseData.data.allservicecases.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            5: { gridData: action.responseData.data.allservicecases.data, totalCount: action.responseData.data.allservicecases.count, timestamp: Date.now() }
                        }
                    };
                case 6:
                    return {
                        ...state,
                        gridData: action.responseData.data.allusers.data,
                        totalCount: action.responseData.data.allusers.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            6: { gridData: action.responseData.data.allusers.data, totalCount: action.responseData.data.allusers.count, timestamp: Date.now() }
                        }
                    };
                case 7:
                    return {
                        ...state,
                        gridData: action.responseData.data.allroles.data,
                        totalCount: action.responseData.data.allroles.count,
                        isLoading: false,
                        error: undefined,
                        selectedCategory: action.selectedCategory,
                        cache: {
                            ...state.cache,
                            7: { gridData: action.responseData.data.allroles.data, totalCount: action.responseData.data.allroles.count, timestamp: Date.now() }
                        }
                    };
            }
            break;
        case errorType:
            return {
                ...state,
                isLoading: false,
                error: action.e
            };
    }
    return state;
};

// Helper to convert cached payload back to the expected response shape
function mapCachedToResponse(categoryId: number, cached: { gridData: any; totalCount?: number }) {
    const data = { data: cached.gridData, count: cached.totalCount };
    switch (categoryId) {
        case 0: return { data: { myopenservicecases: data } };
        case 1: return { data: { myclosedservicecases: data } };
        case 2: return { data: { mycompanyopenservicecases: data } };
        case 3: return { data: { mycompanyclosedservicecases: data } };
        case 4: return { data: { mylicenses: data } };
        case 5: return { data: { allservicecases: data } };
        case 6: return { data: { allusers: data } };
        case 7: return { data: { allroles: data } };
        default: return {} as any;
    }
}

